import React, { useEffect } from 'react';
import {
  Button,
  ButtonGroup,
  Divider,
  Grid,
  LinearProgress,
  Table,
  TableBody,
  TableHead,
  TableRow,
} from '@material-ui/core';
import { useMediaQuery } from 'react-responsive';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import Chart from 'react-apexcharts';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import { storeContext } from '../provider/Provider';
import { websiteAnalyticsActions } from '../../store/website-analytics-reducer';
import chartOptions from '../../customUI/ChartOptions';
import { StyledTableCellGmb, StyledTableHeadCellGmb } from '../../customUI/table/TableStyles';
import InfoCard from '../info-card/InfoCard';
import MobileTitle from '../../customUI/MobileTitle';

const { REACT_APP_FLASK_API_URL } = process.env;

const useStyles = makeStyles((theme) => ({
  toolbar: theme.mixins.toolbar,
  lead__flex: {
    display: 'flex',
  },
  flex__wrap: {
    display: 'flex',
    flexWrap: 'nowrap',
    width: '100%',
  },
  flex__item: {
    flexGrow: 1,
    margin: 15,
    width: '30%',
  },
  flex__item_100: {
    flex: 'auto 1',
    margin: 15,
    // width: "100%",
  },
  flex__item_152: {
    // flexGrow: 2,
    width: 152,
  },
  marginBottom: {
    margin: '0 0 15px 0',
  },
  marginTop: {
    margin: '15px 0 0 0',
  },
  marginVertical: {
    margin: '15px 0',
  },
  gridWrapper: {
    // padding: '0 15px',
    // margin: '15px 0',
  },
  tableChange: {
    color: '#858585',
    margin: '0 5px',
  },
  colorPrimary: {
    backgroundColor: '#E5E5E5',
  },
  barColorPrimary: {
    backgroundColor: '#109CF1',
  },
  wrapper: {
    margin: 20,
    [theme.breakpoints.down('sm')]: {
      margin: '0 5px',
    },
  },
  card: {
    padding: 15,
    background: 'white',
    borderRadius: '16px',
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    [theme.breakpoints.down('sm')]: {
      padding: 5,
      margin: 3,
      marginBottom: 15,
    },
  },
  heading: {
    padding: '0 0 10px 16px',
    fontSize: 20,
    fontWeight: 700,
    color: '#58595B',
    [theme.breakpoints.down('sm')]: {
      fontSize: 18,
      padding: '0 0 5px 5px',
    },
  },
  card__wrapper: {
    padding: '0 10px',
    alignSelf: 'center',
  },
  table__card: {
    padding: '10px 0',
    background: 'white',
    borderRadius: '16px',
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    [theme.breakpoints.down('sm')]: {
      margin: 10,
      marginBottom: 15,
    },
  },
  cardActionButton: {
    padding: '15px 0 5px 0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    fontSize: '14px',
    cursor: 'pointer',
  },
  viewAllText: {
    fontSize: 15,
    userSelect: 'none',
  },
  responsiveTable: {
    overflowX: 'scroll',
  },
  buttonGroupWrapper: {
    textAlign: 'center',
    width: '100%',
  },
  mobileCard: {
    [theme.breakpoints.down('sm')]: {
      width: '50%',
      display: 'inline-block',
    },
  },
}));

const AnalyticsGoogleMyBusiness = () => {
  const isPhone = useMediaQuery({ query: '(max-width: 768px)' });
  const { state, dispatch } = React.useContext(storeContext);
  const classes = useStyles();
  const history = useHistory();

  useEffect(() => {
    dispatch({
      type: websiteAnalyticsActions.WEBSITE_ANALYTICS_SET_IS_FETCHING,
      payload: { isFetching: true },
    });

    axios
      .get(`${REACT_APP_FLASK_API_URL}/v1/gmb/${state.websiteSwitch.currentWebsite}`)
      .then((res) => {
        if (!res.data.error) {
          dispatch({
            type: websiteAnalyticsActions.WEBSITE_ANALYTICS_SET_GMB,
            payload: {
              googleMyBusiness: res.data.data,
              isFetching: false,
            },
          });
        }
      })
      .catch();
  }, []);

  const searchViewsMapViewsChartOptions = {
    ...chartOptions,
    stroke: {
      curve: 'smooth',
      width: 3,
    },
    xaxis: {
      type: 'datetime',
    },
    grid: {
      strokeDashArray: 3,
    },
    colors: ['#B83A94', '#00B5E2'],
    legend: {
      position: 'top',
      horizontalAlign: 'right',
      markers: {
        radius: 0,
        width: 20,
        height: 10,
      },
    },
  };
  const directSearchesDiscoverySearchesChartOptions = {
    ...searchViewsMapViewsChartOptions,
    colors: ['#F4931F', '#00B5E2'],
  };

  const actionsChartOptions = {
    ...directSearchesDiscoverySearchesChartOptions,
    stroke: {
      curve: 'straight',
      width: 3,
    },
    markers: {
      hover: {
        size: 10,
      },
    },
    colors: ['#F4931F', '#00B5E2', '#898989'],
  };

  const gmbConfig = {
    searchViewsMapViewsChartOptions,
    directSearchesDiscoverySearchesChartOptions,
    actionsChartOptions,
  };

  const handleViewAllLocationsButtonClick = () => {
    history.push('/analytics/gmb/locations');
  };

  if (state.websiteAnalytics.isFetching) {
    return (
      <LinearProgress
        style={{ width: '100%' }}
        classes={{
          colorPrimary: classes.colorPrimary,
          barColorPrimary: classes.barColorPrimary,
        }}
      />
    );
  }

  return (
    <>
      {isPhone && (
        <>
          <MobileTitle title="Analytics" />
          <div className={classes.buttonGroupWrapper}>
            <ButtonGroup variant="text">
              <Button
                color="primary"
                onClick={() => {
                  history.push('/analytics/gmb');
                }}
              >
                Google My Business
              </Button>
              <Button
                onClick={() => {
                  history.push('/analytics/semrush');
                }}
              >
                SemRush
              </Button>
            </ButtonGroup>
          </div>
        </>
      )}
      <div className={classes.wrapper}>
        <div className={classes.gridWrapper}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={12} lg={6}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={7} lg={9}>
                  <div className={classes.card}>
                    <div className={classes.heading}>Search Views / Map Views</div>
                    <Chart
                      options={gmbConfig.searchViewsMapViewsChartOptions}
                      series={
                        state.websiteAnalytics.googleMyBusinessData.search_views_map_views_chart
                      }
                      type="line"
                      height="235"
                    />
                  </div>
                </Grid>
                <Grid item xs={12} md={5} lg={3}>
                  <div className={`${classes.marginBottom} ${classes.mobileCard}`}>
                    <InfoCard
                      name="Search Views"
                      percentage={state.websiteAnalytics.googleMyBusinessData.search_views_card.change.toString()}
                      value={state.websiteAnalytics.googleMyBusinessData.search_views_card.current_value.toString()}
                      chartColor="#bb1ddb"
                      chartType="bar"
                      reversed
                      centered
                      isFetching={false}
                    />
                  </div>
                  <div className={`${classes.marginBottom} ${classes.mobileCard}`}>
                    <InfoCard
                      name="Map Views"
                      percentage={state.websiteAnalytics.googleMyBusinessData.map_views_card.change.toString()}
                      value={state.websiteAnalytics.googleMyBusinessData.map_views_card.current_value.toString()}
                      chartColor="#bb1ddb"
                      chartType="bar"
                      reversed
                      centered
                      isFetching={false}
                    />
                  </div>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={12} lg={6}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={7} lg={9}>
                  <div className={classes.card}>
                    <div className={classes.heading}>Direct Searches / Discovery Searches</div>
                    <Chart
                      options={gmbConfig.directSearchesDiscoverySearchesChartOptions}
                      series={
                        state.websiteAnalytics.googleMyBusinessData
                          .direct_searches_discovery_searches_chart
                      }
                      type="line"
                      height="235"
                    />
                  </div>
                </Grid>
                <Grid item xs={12} md={5} lg={3}>
                  <div className={`${classes.marginBottom} ${classes.mobileCard}`}>
                    <InfoCard
                      name="Direct Searches"
                      percentage={state.websiteAnalytics.googleMyBusinessData.direct_searches_card.change.toString()}
                      value={state.websiteAnalytics.googleMyBusinessData.direct_searches_card.current_value.toString()}
                      chartColor="#bb1ddb"
                      chartType="bar"
                      reversed
                      centered
                      isFetching={false}
                    />
                  </div>
                  <div className={`${classes.marginBottom} ${classes.mobileCard}`}>
                    <InfoCard
                      name="Discovery Searches"
                      percentage={state.websiteAnalytics.googleMyBusinessData.discovery_searches_card.change.toString()}
                      value={state.websiteAnalytics.googleMyBusinessData.discovery_searches_card.current_value.toString()}
                      chartColor="#bb1ddb"
                      chartType="bar"
                      reversed
                      centered
                      isFetching={false}
                    />
                  </div>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </div>
        <div className={classes.gridWrapper}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={12} lg={6}>
              <div className={classes.card}>
                <div className={classes.heading}>Google My Business Actions</div>
                <Chart
                  options={gmbConfig.actionsChartOptions}
                  series={state.websiteAnalytics.googleMyBusinessData.actions_chart}
                  type="line"
                  height="285"
                />
              </div>

              <div className={classes.marginTop}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <InfoCard
                      name="Website Actions"
                      percentage={state.websiteAnalytics.googleMyBusinessData.totals.change.website_actions.toString()}
                      value={state.websiteAnalytics.googleMyBusinessData.totals.current_values.website_actions.toString()}
                      chartColor="#bb1ddb"
                      chartType="bar"
                      reversed
                      centered
                      inline={!isPhone}
                      isFetching={false}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <InfoCard
                      name="GMB Calls"
                      percentage={state.websiteAnalytics.googleMyBusinessData.totals.change.phone_call_actions.toString()}
                      value={state.websiteAnalytics.googleMyBusinessData.totals.current_values.phone_call_actions.toString()}
                      chartColor="#bb1ddb"
                      chartType="bar"
                      reversed
                      centered
                      inline={!isPhone}
                      isFetching={false}
                    />
                  </Grid>
                </Grid>
              </div>
            </Grid>
            <Grid item xs={12} md={12} lg={6}>
              <div className={classes.card}>
                <div className={`${classes.heading} ${classes.marginVertical}`}>
                  Google My Business By Location
                </div>
                <div className={classes.responsiveTable}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <StyledTableHeadCellGmb>Store Code</StyledTableHeadCellGmb>
                        <StyledTableHeadCellGmb>Website Actions</StyledTableHeadCellGmb>
                        <StyledTableHeadCellGmb>Phone Call Actions</StyledTableHeadCellGmb>
                        <StyledTableHeadCellGmb>Total Views</StyledTableHeadCellGmb>
                        <StyledTableHeadCellGmb>Total Searches</StyledTableHeadCellGmb>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {state.websiteAnalytics.googleMyBusinessData.locations_table
                        .slice(0, 10)
                        .map((row) => (
                          <TableRow key={row.id}>
                            <StyledTableCellGmb component="td" scope="row">
                              {row.id}. {row.store_code}
                            </StyledTableCellGmb>
                            <StyledTableCellGmb component="td" scope="row">
                              {row.website_actions}{' '}
                              <span className={classes.tableChange}>
                                {row.website_actions_change}%
                              </span>
                            </StyledTableCellGmb>
                            <StyledTableCellGmb component="td" scope="row">
                              {row.phone_call_actions}{' '}
                              <span className={classes.tableChange}>
                                {row.phone_call_actions_change}%
                              </span>
                            </StyledTableCellGmb>
                            <StyledTableCellGmb component="td" scope="row">
                              {row.total_views}{' '}
                              <span className={classes.tableChange}>{row.total_views_change}%</span>
                            </StyledTableCellGmb>
                            <StyledTableCellGmb component="td" scope="row">
                              {row.total_searches}{' '}
                              <span className={classes.tableChange}>
                                {row.total_searches_change}%
                              </span>
                            </StyledTableCellGmb>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </div>
                <Divider />
                <div className={classes.card__wrapper}>
                  <div
                    className={classes.cardActionButton}
                    onClick={handleViewAllLocationsButtonClick}
                    aria-hidden="true"
                    aria-label="viewAllBtn"
                  >
                    <div className={classes.viewAllText}>View All</div>
                    <ArrowForwardIosIcon style={{ paddingLeft: 10, fontSize: '11px' }} />
                  </div>
                </div>
              </div>
            </Grid>
          </Grid>
        </div>
      </div>
    </>
  );
};

export default AnalyticsGoogleMyBusiness;
